import { SessionGuard } from './core/session/session.guard';
import { NgModule } from '@angular/core';
import { Data, ExtraOptions, Route, RouterModule } from '@angular/router';
import { PageNotFoundComponent } from './pages/public/page-not-found/page-not-found.component';
import { SbForgotPasswordComponent } from './pages/public/sb-forgot-password/sb-forgot-password.component';
import { SbGridComponent } from './legacy/sb-grid/sb-grid.component';
import { SbResetPasswordComponent } from './pages/public/sb-reset-password/sb-reset-password.component';
import { SbUploadWithPreviewComponent } from './legacy/sb-upload-with-preview/sb-upload-with-preview.component';
import { FileUploadComponent } from './legacy/sb-upload/file-upload-component/file-upload.component';
import { LoginComponent } from './pages/public/login/login.component';
import { KeycloakGuard } from './core/session/keycloak-session.guard';
import { AutoLoginGuard } from './core/session/auto-login.guard';
import { LandingPageResolver } from '@resolvers';
import { KeycloakRoleGuard } from './core/session/role-keycloak.guard';
import { RoleGuard } from './core/session/role.guard';
import { TwoFactorGuard } from './pages/public/login/two-factor/two-factor.guard';
import { ModalComponent } from './features/modal/components/modal.component';
import { DialogConfig } from './pages/authenticated/private-equity/modals/dialog-routing.module';
import { AdminRoleGuard } from './core/session/admin-role.guard';
import { AccountingBookResolver } from './pages/authenticated/accounting/resolvers/accounting-book.resolver';
import { SbAccountingCalculationsTriggerComponent } from './pages/authenticated/accounting/components/sb-accounting-calculations-trigger/sb-accounting-calculations-trigger.component';
import { AccessRightGuard } from './core/session/access-right.guard';
import { KeycloakAccessRightGuard } from './core/session/keycloak-access-right.guard';
import { PageEmptyComponent } from './pages/public/page-empty/page-empty.component';
import { PositionsTableComponent } from './pages/authenticated/positions/components/positions-table/positions-table.component';
import { BreadcrumbResolver } from './shared/components/page/sb-breadcrumbs/breadcrumb.resolver';
import { preserveQueryParamsGuard } from './features/data-entry-flow/guards/query-params.guard';

type SBRoutes = Array<SBRoute>;
interface SBRoute extends Route {
    data?: SBData;
}
interface SBData extends Data {
    modals?: Array<DataEntryOption>;
}
interface DataEntryOption {
    name: string;
    icon: string;
    description: string;
    url?: string;
    id: string;
    status?: 'coming soon';
}

export const routes: SBRoutes = [
    {
        path: '',
        resolve: [LandingPageResolver],
        // Can activate here since we may bypass "login" through SSO from other apps.
        canActivate: [SessionGuard, KeycloakGuard],
        component: PageEmptyComponent
    },
    {
        path: 'upload',
        canActivate: [SessionGuard, KeycloakGuard],
        component: FileUploadComponent
    },
    {
        path: 'uploadwithpreview',
        canActivate: [SessionGuard, KeycloakGuard],
        component: SbUploadWithPreviewComponent
    },
    {
        path: 'equity',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import('./pages/authenticated/equity/equity.module').then(
                (m) => m.EquityModule
            )
    },
    {
        path: 'transfer',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import(
                './pages/authenticated/general/transaction-forms/transfer/transfer.module'
            ).then((m) => m.TransferModule)
    },
    {
        path: 'subscription-right',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import(
                './pages/authenticated/subscription-right/transaction-forms/issue/subscription-right-issue.module'
            ).then((m) => m.SubscriptionRightIssueModule)
    },
    {
        path: 'subscription-right/convert-to-cash',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import(
                './pages/authenticated/subscription-right/transaction-forms/convert-to-cash/subscription-right-convert-to-cash.module'
            ).then((m) => m.SubscriptionRightConvertToCashModule)
    },
    {
        path: 'subscription-right/convert-to-equity',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import(
                './pages/authenticated/subscription-right/transaction-forms/convert-to-equity/subscription-right-convert-to-equity.module'
            ).then((m) => m.SubscriptionRightConvertToEquityModule)
    },
    {
        path: 'subscription-right/convert-to-right',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import(
                './pages/authenticated/subscription-right/transaction-forms/convert-to-right/subscription-right-convert-to-right.module'
            ).then((m) => m.SubscriptionRightConvertToRightModule)
    },
    {
        path: 'fx',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import('./pages/authenticated/fx/fx.module').then((m) => m.FxModule)
    },
    {
        path: 'sb-grid/:gridPath',
        canActivate: [SessionGuard, KeycloakGuard],
        component: SbGridComponent,
        loadChildren: () =>
            import('./legacy/price-and-valuation/private-equity.module').then(
                (m) => m.PrivateEquityTempModule
            )
    },
    {
        path: 'sb-grid/:gridPath/:id',
        canActivate: [SessionGuard, KeycloakGuard],
        component: SbGridComponent
    },
    { path: 'login', component: LoginComponent, canActivate: [AutoLoginGuard] },
    {
        path: 'two-factor',
        canActivate: [TwoFactorGuard],
        loadComponent: () =>
            import('./pages/public/login/two-factor/two-factor.component').then(
                (m) => m.TwoFactorComponent
            ),
        children: [
            {
                path: 'mobile-app',
                loadComponent: () =>
                    import(
                        './pages/public/login/two-factor/mobile-app/mobile-app.component'
                    ).then((m) => m.MobileAppComponent)
            }
        ]
    },
    { path: 'forgot-password', component: SbForgotPasswordComponent },
    { path: 'reset-password', component: SbResetPasswordComponent },
    {
        path: 'logout',
        outlet: 'modal',
        canActivate: [SessionGuard, KeycloakGuard, preserveQueryParamsGuard],

        loadChildren: () =>
            import('./core/session/logout/logout.module').then((m) => m.LogoutModule)
    },
    {
        path: 'valuation',
        outlet: 'modal',
        loadChildren: () =>
            import('./legacy/price-and-valuation/price-and-valuation.module').then(
                (m) => m.PriceAndValuationModule
            )
    },
    {
        path: 'charts',
        loadChildren: () =>
            import('./pages/public/chart-exploration/chart-exploration.module').then(
                (m) => m.ChartExplorationModule
            )
    },
    {
        path: 'pdf-reader',
        loadComponent: () =>
            import('./pages/public/pdf-reader/pdf-reader.component').then(
                (m) => m.PdfReaderComponent
            )
    },
    {
        path: 'account-list',
        loadComponent: () =>
            import('./shared/components/sb-account-list/sb-account-list.component').then(
                (m) => m.SbAccountListComponent
            )
    },
    {
        path: 'pe-funds',
        redirectTo: 'private-equity-fund'
    },
    {
        path: 'private-equity-fund',
        canActivate: [SessionGuard, KeycloakGuard, RoleGuard, KeycloakRoleGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            roles: ['PRIVATE_EQUITY_FUNDS_VIEWER'],
            breadcrumb: 'Private Equity Funds',
            actions: [
                {
                    label: 'Add new',
                    url: [
                        '',
                        {
                            outlets: {
                                modal: ['data-entry', 'private-equity-fund']
                            }
                        }
                    ],
                    icon: 'plus'
                }
                // TODO: re-add tutorial action
                /*{
                    label: 'Help',
                    url: [
                        '',
                        {
                            outlets: {
                                modal: ['data-entry', 'private-equity-fund']
                            }
                        }
                    ],
                    class: 'btn link text-primary',
                    icon: 'help'
                }*/
            ],
            modals: [
                {
                    name: 'Fund transaction',
                    icon: 'scale',
                    description: 'Distribution, Draw Down or Returned Contribution.',
                    url: 'buy-sell',
                    id: 'buy-sell'
                },
                {
                    name: 'Fund valuation',
                    icon: 'timeline',
                    description: 'Add value of your NAV in the fund.',
                    url: 'valuation',
                    id: 'valuation'
                },
                {
                    name: 'Fund details',
                    icon: 'edit',
                    description: 'Need to make a change? Edit fund details.',
                    url: 'edit',
                    id: 'edit'
                }
            ]
        },
        loadChildren: () =>
            import('./pages/authenticated/pe-funds/pe-funds.module').then(
                (m) => m.PeFundsModule
            )
    },
    {
        path: 'private-assets',
        canActivate: [SessionGuard, KeycloakGuard, RoleGuard, KeycloakRoleGuard],
        data: { roles: ['PRIVATE_ASSETS_VIEWER'] },
        loadChildren: () =>
            import('./pages/authenticated/private-asset/private-asset.module').then(
                (m) => m.PrivateAssetModule
            )
    },
    {
        path: 'fixed-income',
        canActivate: [SessionGuard, KeycloakGuard, RoleGuard, KeycloakRoleGuard],
        /*loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),*/
        data: {
            roles: ['FIXED_INCOME_VIEWER'],
            breadcrumb: 'Fixed Income',
            actions: [
                {
                    label: 'Add new',
                    url: [
                        '',
                        {
                            outlets: {
                                modal: ['data-entry', 'fixed-income']
                            }
                        }
                    ],
                    icon: 'plus'
                }
            ],
            modals: [
                {
                    name: 'Bond transaction',
                    icon: 'scale',
                    description: 'Bought or sold bonds.',
                    //url: 'add',
                    id: 'add',
                    status: 'coming soon'
                },
                {
                    name: 'Coupon Transaction',
                    icon: 'notes',
                    description: 'Coupon payments for your bonds.',
                    //url: 'coupon-payment',
                    id: 'coupon-payment',
                    status: 'coming soon'
                },
                {
                    name: 'Handle Bond',
                    icon: 'edit',
                    description: 'Edit static data for your bonds.',
                    url: 'edit',
                    id: 'edit'
                }
            ]
        },
        loadChildren: () =>
            import('./pages/authenticated/fixed-income/fixed-income.module').then(
                (m) => m.FixedIncomeModule
            )
    },
    {
        // Move this route to fixed-income routing module once fixed income page is complete
        path: 'fi/add-transaction/bond',
        canActivate: [AccessRightGuard, KeycloakAccessRightGuard],
        data: { accessRights: ['REGISTER_TRANSACTION'] },
        loadComponent: () =>
            import(
                './pages/authenticated/fixed-income/forms/sb-add-bond/sb-add-bond.component'
            ).then((m) => m.SbAddBondComponent)
    },
    {
        path: 'dropbox',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import('./shared/dropbox/dropbox.module').then((m) => m.DropboxModule)
    },
    {
        path: 'admin',
        canActivate: [SessionGuard, KeycloakGuard, RoleGuard, KeycloakRoleGuard],
        data: {
            roles: ['ADMINISTRATOR'],
            hideBreadcrumbs: false,
            breadcrumb: 'skip'
        },
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        loadChildren: () =>
            import('./pages/admin/admin-routing.module').then((m) => m.AdminRoutingModule)
    },
    {
        path: 'impersonation',
        canActivate: [SessionGuard, KeycloakGuard, RoleGuard, KeycloakRoleGuard],
        data: {
            roles: ['ACT_ON_BEHALF'],
            hideBreadcrumbs: true,
            breadcrumb: 'skip',
            containerSize: 'xxl'
        },
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        loadChildren: () =>
            import(
                './pages/authenticated/impersonation/impersonation-routing.module'
            ).then((m) => m.ImpersonationRoutingModule)
    },
    {
        path: 'equities',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Equity'
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('./pages/authenticated/companies/equity.module').then(
                        (m) => m.EquityModule
                    )
            }
        ]
    },
    {
        path: 'private-equity',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Private Equity',
            actions: [
                {
                    label: 'Add new',
                    url: [
                        '',
                        {
                            outlets: {
                                modal: ['data-entry', 'private-equity']
                            }
                        }
                    ],
                    icon: 'plus'
                }
            ],
            modals: [
                {
                    name: 'Share transaction',
                    icon: 'scale',
                    description: 'Bought or sold shares.',
                    url: 'share-transaction',
                    id: 'share-transaction'
                },
                {
                    name: 'Company valuation',
                    icon: 'timeline',
                    description: 'Add price and total quantity to measure performance.',
                    id: 'valuation',
                    url: 'valuation'
                },
                {
                    name: 'Convertible Note',
                    icon: 'notes',
                    description: 'Enter or exit a convertible note agreement.',
                    url: 'convertible-note',
                    id: 'convertible-note'
                },
                {
                    name: 'Option transaction',
                    icon: 'premium-rights',
                    description:
                        'Register purchase or sale of call options in the company.',
                    url: 'option',
                    id: 'option'
                },
                {
                    name: 'Shareholder Contribution',
                    icon: 'cash-banknote',
                    description: 'Register contribution or pay back.',
                    url: 'shareholder-contribution',
                    id: 'shareholder-contribution'
                },
                {
                    name: 'New loan',
                    icon: 'heart-handshake',
                    description: 'Register a loan you gave the company.',
                    url: 'new-loan',
                    id: 'new-loan'
                    // status: 'coming soon'
                },
                ,
                {
                    name: 'New share class',
                    icon: 'category-plus',
                    description: 'Register a new share/stock class.',
                    url: 'share-class',
                    id: 'share-class'
                    // status: 'coming soon'
                },
                {
                    name: 'Loan payment',
                    icon: 'moneybag',
                    description: 'Payment for existing loan.',
                    url: 'handle-existing-loan',
                    id: 'magic-link'
                },
                {
                    name: 'Corporate action',
                    icon: 'timeline-event',
                    description: 'Dividends, rights, splits etc.',
                    //url: 'corporate-action',
                    id: 'corporate-action',
                    status: 'coming soon'
                } /*
                {
                    name: 'Cost transaction',
                    icon: 'premium-rights',
                    description: 'Add fee, interest etc. associated with investment.',
                    url: 'cost',
                    id: 'cost'
                },*/
            ]
        },
        loadChildren: () =>
            import(
                './pages/authenticated/private-equity/private-equity-routing.module'
            ).then((m) => m.PrivateEquityRoutingModule)
    },
    {
        path: 'account-statement',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Account statement'
        },
        loadChildren: () =>
            import(
                './pages/authenticated/account-statement/account-statement-routing.module'
            ).then((m) => m.AccountStatementRoutingModule)
    },
    {
        path: 'documents',
        canActivate: [SessionGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Documents',
            actions: [
                {
                    label: 'Upload files',
                    url: [
                        '',
                        {
                            outlets: {
                                dialog: ['data-entry', 'document', 'upload']
                            }
                        }
                    ],
                    icon: 'upload'
                }
            ]
        },
        loadChildren: () =>
            import('./pages/authenticated/documents/documents-routing.module').then(
                (m) => m.DocumentsRoutingModule
            )
    },
    {
        canActivate: [SessionGuard, KeycloakGuard],
        path: 'recon',
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Reconciliation',
            roles: ['PORTFOLIO_RECON_ADMINISTRATOR']
        },
        loadChildren: () =>
            import('./pages/authenticated/recon/recon-routing.module').then(
                (m) => m.ReconRoutingModule
            )
    },
    {
        canActivate: [SessionGuard, KeycloakGuard],
        path: 'accounting',
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Accounting',
            actions: [
                {
                    label: 'Active book',
                    type: 'select'
                },
                {
                    label: 'Run calculations',
                    type: 'component',
                    component: SbAccountingCalculationsTriggerComponent
                },
                {
                    label: '',
                    url: [
                        '',
                        {
                            outlets: {
                                modal: ['settings', 'accounting']
                            }
                        }
                    ],
                    icon: 'settings',
                    class: 'd-inline-flex link',
                    tooltip: 'Open accounting settings'
                }
            ]
        },
        resolve: { pageSelect: AccountingBookResolver },
        loadChildren: () =>
            import('./pages/authenticated/accounting/accounting-routing.module').then(
                (m) => m.AccountingRoutingModule
            )
    },
    {
        canActivate: [SessionGuard, preserveQueryParamsGuard],
        outlet: 'modal',
        path: 'settings',
        loadChildren: () =>
            import('./features/settings/settings.module').then((m) => m.SettingsModule)
    },
    {
        canActivate: [SessionGuard, KeycloakGuard],
        outlet: 'dialog',
        path: 'mfa',
        loadChildren: () =>
            import('./features/multi-factor-authentication/mfa-routing.module').then(
                (m) => m.MfaRoutingModule
            )
    },
    {
        path: 'commercial-real-estate',
        canActivate: [SessionGuard, KeycloakGuard, RoleGuard, KeycloakRoleGuard],
        loadChildren: () =>
            import(
                './pages/authenticated/commercial-real-estate/commercial-real-estate.module'
            ).then((m) => m.CommercialRealEstateModule),
        data: {
            roles: ['COMMERCIAL_REAL_ESTATE_VIEWER'],
            breadcrumb: 'Commercial Real Estate',
            actions: [
                {
                    label: 'Add new',
                    url: [
                        '',
                        {
                            outlets: {
                                modal: ['data-entry', 'commercial-real-estate']
                            }
                        }
                    ],
                    icon: 'plus'
                }
            ],
            modals: [
                {
                    name: 'New loan',
                    icon: 'heart-handshake',
                    description: 'Register a loan for property.',
                    url: 'new-loan',
                    id: 'new-loan'
                },
                {
                    name: 'Interest payment',
                    icon: 'percentage',
                    description: 'Add an interest payment for a loan of a property.',
                    url: 'interest',
                    id: 'interest'
                },
                {
                    name: 'Loan amortization payment',
                    icon: 'send',
                    description: 'Register an amortization payment of a loan.',
                    url: 'handle-existing-loan',
                    id: 'loan-payment'
                },
                {
                    name: 'Cost & refurbishment',
                    icon: 'premium-rights',
                    description:
                        'Add any cost, fee or refurbishment transactions associated with property.',
                    url: 'cost',
                    id: 'cost'
                },
                {
                    name: 'Invest more / Sell off',
                    icon: 'premium-rights',
                    description:
                        'Add a buy or sell transactions associated with property.',
                    url: 'buy-sell',
                    id: 'buy-sell'
                },
                {
                    name: 'Income / revenue',
                    icon: 'pig-money',
                    description:
                        'Add rent or any income transactions associated with property.',
                    url: 'income',
                    id: 'income'
                },
                {
                    name: 'Valuation, area & occupancy',
                    icon: 'timeline',
                    description:
                        'Add valuation, occupancy data, total area, economic area to track over time.',
                    url: 'valuation',
                    id: 'valuation'
                },
                {
                    name: 'Edit',
                    icon: 'edit',
                    description: 'Edit commercial real estate data.',
                    url: 'edit',
                    id: 'edit'
                }
            ]
        }
    },
    {
        path: 'financing',
        canActivate: [SessionGuard, KeycloakGuard, RoleGuard, KeycloakRoleGuard],
        loadChildren: () =>
            import('./pages/authenticated/credits/credits.module').then(
                (m) => m.CreditsModule
            ),
        data: {
            roles: ['CREDITS_VIEWER'],
            breadcrumb: 'Financing'
        }
    },
    {
        path: 'dividends',
        canActivate: [SessionGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
            ).then((m) => m.SbModalDialogComponent),
        data: {
            name: 'Dividends',
            description: 'Register dividends.',
            modals: [
                {
                    name: 'Cash dividend',
                    icon: 'dollar', //TODO: Check the icon
                    description: 'Add a new cash dividend',
                    url: 'cash-dividend',
                    id: 'cash-dividend'
                },
                {
                    name: 'Asset dividend',
                    icon: 'hexagon', //TODO: Check the icon
                    description: 'Add a new asset dividend',
                    url: 'asset-dividend',
                    id: 'asset-dividend'
                }
            ]
        }
    },
    {
        path: 'loans',
        canActivate: [SessionGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
            ).then((m) => m.SbModalDialogComponent),
        data: {
            name: 'Loans',
            description: 'Register loans.',
            modals: [
                {
                    name: 'Register new loan',
                    description: 'Add a new cash loan',
                    url: 'register-loan',
                    id: 'register-loan',
                    icon: 'premium-rights'
                },
                {
                    name: 'Handle existing loan',
                    description: 'Handle an existing cash loan',
                    url: 'handle-existing-loan',
                    id: 'handle-existing-loan',
                    icon: 'edit'
                },
                {
                    name: 'Edit existing loan',
                    description: 'Edit an existing cash loan',
                    url: 'edit-existing-loan',
                    id: 'edit-existing-loan',
                    icon: 'adjustments-alt'
                }
            ]
        }
    },
    {
        path: 'deposits',
        canActivate: [SessionGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
            ).then((m) => m.SbModalDialogComponent),
        data: {
            name: 'Deposits',
            description: 'Register Deposits.',
            modals: [
                {
                    name: 'Register new deposit',
                    description: 'Add a new deposit',
                    url: 'register-deposit',
                    id: 'register-deposit',
                    icon: 'heart-handshake'
                },
                {
                    name: 'Close deposit',
                    description: 'Close an existing deposit',
                    url: 'close-existing-deposit',
                    id: 'close-existing-deposit',
                    icon: 'premium-rights'
                },
                {
                    name: 'Increase/Decrease deposit',
                    description: 'Increase or decrease an existing deposit',
                    url: 'handle-existing-deposit',
                    id: 'handle-existing-deposit',
                    icon: 'edit'
                },
                {
                    name: 'Edit deposit',
                    description: 'Edit an existing deposit',
                    // url: 'edit-existing-deposit',
                    id: 'edit-existing-deposit',
                    icon: 'adjustments-alt',
                    status: 'coming soon'
                }
            ]
        }
    },
    {
        path: 'categories',
        canActivate: [SessionGuard, KeycloakGuard],
        loadChildren: () =>
            import('./pages/authenticated/categories/categories.module').then(
                (m) => m.CategoriesModule
            )
    },
    {
        path: 'action',
        outlet: 'dialog',
        canActivate: [SessionGuard, KeycloakGuard],
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('./features/modal/modal.module').then((m) => m.ModalModule)
            },
            {
                path: 'categories',
                loadChildren: () =>
                    import('./pages/authenticated/categories/modals/modals.module').then(
                        (m) => m.ModalsModule
                    )
            },
            {
                path: 'futures',
                loadChildren: () =>
                    import(
                        './pages/authenticated/futures/modals/secondary-modals.module'
                    ).then((m) => m.SecondaryModalsModule)
            },
            {
                path: 'interest-transaction',
                canActivate: [SessionGuard],
                loadComponent: () =>
                    import(
                        './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
                    ).then((m) => m.SbModalDialogComponent),
                data: {
                    name: 'Interest',
                    description: 'Register Interest'
                }
            }
        ]
    },
    {
        path: 'action',
        outlet: 'modal',
        canActivate: [SessionGuard, KeycloakGuard],
        children: [
            {
                path: 'commercial-real-estate',
                loadChildren: () =>
                    import(
                        './pages/authenticated/commercial-real-estate/modals/modals.module'
                    ).then((m) => m.ModalsModule)
            },
            {
                path: 'futures',
                loadChildren: () =>
                    import('./pages/authenticated/futures/modals/modals.module').then(
                        (m) => m.ModalsModule
                    )
            },
            {
                path: 'loans',
                loadChildren: () =>
                    import('./shared/forms/loans/modals/loans-modal.module').then(
                        (m) => m.LoansModalsModule
                    )
            },
            {
                path: 'deposits',
                loadChildren: () =>
                    import('./shared/forms/deposits/modals/deposits-modal.module').then(
                        (m) => m.DepositsModalsModule
                    )
            },
            {
                path: 'interest-transaction',
                loadChildren: () =>
                    import(
                        './shared/forms/interest-transaction/interest-transaction-modals.module'
                    ).then((m) => m.InterestTransactionModalsModule)
            },
            {
                path: 'transactions',
                loadChildren: () =>
                    import(
                        './pages/authenticated/transactions/modals/modal-routing.module'
                    ).then((m) => m.ModalRoutingModule)
            },
            {
                path: 'reporting',
                loadChildren: () =>
                    import(
                        './pages/authenticated/reporting/reporting-routing.module'
                    ).then((m) => m.ReportingRoutingModule)
            },
            {
                path: 'asset',
                loadChildren: () =>
                    import(
                        './pages/authenticated/private-asset/actions/actions.module'
                    ).then((m) => m.ActionsModule)
            },
            {
                path: 'categories',
                loadChildren: () =>
                    import('./pages/authenticated/categories/modals/modals.module').then(
                        (m) => m.ModalsModule
                    )
            },
            {
                path: 'admin',
                canActivate: [SessionGuard, KeycloakGuard, RoleGuard, KeycloakRoleGuard],
                data: {
                    roles: ['ADMINISTRATOR']
                },
                loadChildren: () =>
                    import('./pages/admin/modals/modals.module').then(
                        (m) => m.ModalsModule
                    )
            },
            {
                path: 'dividends',
                loadChildren: () =>
                    import(
                        './shared/forms/dividends/modals/dividends-modals.module'
                    ).then((m) => m.DividendsModalsModule)
            }
        ]
    },

    {
        path: 'view-transactions',
        outlet: 'modal',
        canActivate: [SessionGuard, KeycloakGuard],
        children: [
            {
                path: 'private-equity',
                //component: ModalComponent,
                loadComponent: () =>
                    import(
                        './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
                    ).then((m) => m.SbModalDialogComponent),
                data: {
                    title: 'Transactions',
                    type: 'main',
                    closeModal: true,
                    closeOnBackdrop: true,
                    showBackdrop: true,
                    size: 'lg',
                    fullscreen: true
                },
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import(
                                './features/transactions/components/sb-list-transactions/sb-list-transactions.component'
                            ).then((m) => m.SbListTransactionsComponent)
                    }
                ]
            }
        ]
    },
    {
        path: 'accounting',
        outlet: 'modal',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
            ).then((m) => m.SbModalDialogComponent),
        children: [
            {
                path: 'add-ledger-account',
                loadComponent: () =>
                    import(
                        './pages/authenticated/accounting/forms/sb-add-ledger-account/sb-add-ledger-account.component'
                    ).then((m) => m.SbAddLedgerAccountComponent),
                data: {
                    title: 'Add ledger account',
                    size: 'auto',
                    showBackdrop: true,
                    closeModal: true
                }
            },
            {
                path: 'export-data',
                loadComponent: () =>
                    import(
                        './pages/authenticated/accounting/forms/sb-export-accounting-data/sb-export-accounting-data.component'
                    ).then((m) => m.SbExportAccountingDataComponent),
                data: {
                    title: 'Export data',
                    size: 'auto',
                    showBackdrop: true,
                    closeModal: true
                }
            }
        ]
    },
    {
        path: 'impersonation',
        outlet: 'modal',
        canActivate: [SessionGuard, KeycloakGuard, AdminRoleGuard],
        data: {
            roles: ['ACT_ON_BEHALF']
        },
        children: [
            {
                path: 'switch-user',
                data: {
                    title: 'Switch user',
                    size: 'auto',
                    showBackdrop: true,
                    closeModal: true,
                    fullscreen: true
                },
                loadComponent: () =>
                    import(
                        './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
                    ).then((m) => m.SbModalDialogComponent),
                loadChildren: () =>
                    import('./pages/admin/impersonate/modals/modal-routing.module').then(
                        (m) => m.ModalRoutingModule
                    )
            }
        ]
    },
    {
        path: 'data-entry',
        outlet: 'modal',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-data-entry-flow/sb-data-entry-flow.component'
            ).then((m) => m.SbDataEntryFlowComponent),
        children: [
            {
                path: 'private-equity-fund',
                data: {
                    selectInstrumentHeader: 'Choose existing fund',
                    addNewUrl: [
                        '',
                        {
                            outlets: {
                                dialog: ['data-entry', 'private-equity-fund', 'add-new']
                            }
                        }
                    ]
                },
                loadChildren: () =>
                    import(
                        './pages/authenticated/pe-funds/modals/modal-routing.module'
                    ).then((m) => m.ModalRoutingModule)
            },
            {
                path: 'private-equity',
                data: {
                    selectInstrumentHeader: 'Choose existing company',
                    addNewUrl: [
                        '',
                        {
                            outlets: {
                                dialog: ['data-entry', 'private-equity', 'add-company']
                            }
                        }
                    ]
                },
                loadChildren: () =>
                    import(
                        './pages/authenticated/private-equity/modals/modal-routing.module'
                    ).then((m) => m.ModalRoutingModule)
            },
            {
                path: 'fixed-income',
                data: {
                    selectInstrumentHeader: 'Choose existing bond'
                    //Removes the 'Add' button for Bonds
                    /*addNewUrl: [
                        '',
                        {
                            outlets: {
                                dialog: ['data-entry', 'fixed-income', 'add']
                            }
                        }
                    ]*/
                },
                loadChildren: () =>
                    import(
                        './pages/authenticated/fixed-income/modals/modal-routing.module'
                    ).then((m) => m.ModalRoutingModule)
            },
            {
                path: 'commercial-real-estate',
                data: {
                    selectInstrumentHeader: 'Choose existing property'
                    /*addNewUrl: [
                        '',
                        {
                            outlets: {
                                dialog: [
                                    'data-entry',
                                    'commercial-real-estate',
                                    'add-new'
                                ]
                            }
                        }
                    ]*/
                },
                loadChildren: () =>
                    import(
                        './pages/authenticated/commercial-real-estate/modals/modal-routing.module'
                    ).then((m) => m.ModalRoutingModule)
            },
            {
                path: 'futures',
                loadChildren: () =>
                    import(
                        './pages/authenticated/futures/modals/modal-routing.module'
                    ).then((m) => m.ModalsRoutingModule)
            },
            {
                path: 'accounting',
                loadChildren: () =>
                    import(
                        './pages/authenticated/accounting/modals/modal-routing.module'
                    ).then((m) => m.ModalRoutingModule)
            }
        ]
    },
    {
        path: 'data-entry',
        outlet: 'dialog',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
            ).then((m) => m.SbModalDialogComponent),
        children: [
            {
                path: 'private-equity-fund',
                loadChildren: () =>
                    import(
                        './pages/authenticated/pe-funds/modals/dialog-routing.module'
                    ).then((m) => m.DialogRoutingModule)
            },
            {
                path: 'private-equity',
                loadChildren: () =>
                    import(
                        './pages/authenticated/private-equity/modals/dialog-routing.module'
                    ).then((m) => m.DialogRoutingModule)
            },
            {
                path: 'accounting',
                loadChildren: () =>
                    import(
                        './pages/authenticated/accounting/modals/dialog-routing.module'
                    ).then((m) => m.DialogRoutingModule)
            },
            {
                path: 'document',
                loadChildren: () =>
                    import(
                        './pages/authenticated/documents/modals/dialog-routing.module'
                    ).then((m) => m.DialogRoutingModule)
            },
            {
                path: 'tags',
                loadChildren: () =>
                    import(
                        './pages/authenticated/tags/modals/dialog-routing.module'
                    ).then((m) => m.DialogRoutingModule)
            }
        ]
    },
    {
        path: 'not-found',
        canActivate: [SessionGuard, KeycloakGuard],
        component: PageNotFoundComponent
    },
    {
        path: 'positions',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'skip'
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import(
                        './pages/authenticated/positions/components/positions-table/positions-table.component'
                    ).then((m) => m.PositionsTableComponent),
                data: {
                    breadcrumb: 'Positions'
                }
            }
        ]
    },
    {
        path: 'transactions',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'skip'
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import(
                        './pages/authenticated/transactions/pages/transactions.component'
                    ).then((m) => m.TransactionsPageComponent),
                data: {
                    breadcrumb: 'Transactions'
                }
            }
        ]
    },
    {
        path: 'reporting',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Reporting'
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import(
                        './pages/authenticated/reporting/reporting-routing.module'
                    ).then((m) => m.ReportingRoutingModule),
                data: {
                    breadcrumb: 'Reporting'
                }
            }
        ]
    },
    {
        path: 'allocation',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './pages/authenticated/allocation/components/sb-allocation/sb-allocation.component'
            ).then((m) => m.SbAllocationComponent)
    },
    {
        path: 'tags',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './pages/authenticated/tags/components/sb-tag-list/sb-tag-list.component'
            ).then((m) => m.SbTagListComponent)
    },
    {
        path: 'instruments',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Instrument',
            hideRootBreadcrumb: true
        },
        children: [
            {
                data: {
                    breadcrumb: 'skip'
                },
                path: '',
                loadComponent: () =>
                    import(
                        './pages/authenticated/instruments/components/sb-instrument-list/sb-instrument-list.component'
                    ).then((m) => m.SbInstrumentListComponent)
            }
        ]
    },
    {
        path: 'accounts',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Accounts',
            hideRootBreadcrumb: true
        },
        children: [
            {
                data: {
                    breadcrumb: 'skip'
                },
                path: '',
                loadComponent: () =>
                    import(
                        './pages/authenticated/accounts/components/sb-account-list/sb-account-list.component'
                    ).then((m) => m.SbAccountListComponent)
            }
        ]
    },
    {
        path: 'overview',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Overview',
            hideRootBreadcrumb: true
        },
        children: [
            {
                data: {
                    breadcrumb: 'skip'
                },
                path: '',
                loadComponent: () =>
                    import(
                        './pages/authenticated/overview/views/sb-overview/sb-overview.component'
                    ).then((m) => m.SbOverviewComponent)
            }
        ]
    },
    {
        path: 'exchange-traded-funds',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Funds',
            instrumentType: 'ETF'
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('./pages/authenticated/funds/funds.module').then(
                        (m) => m.FundsModule
                    )
            }
        ]
    },
    {
        path: 'mutual-funds',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Funds',
            instrumentType: 'FUND'
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('./pages/authenticated/funds/funds.module').then(
                        (m) => m.FundsModule
                    )
            }
        ]
    },
    {
        path: 'funds',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Funds'
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('./pages/authenticated/funds/funds.module').then(
                        (m) => m.FundsModule
                    )
            }
        ]
    },
    {
        path: 'cert',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './shared/components/page/sb-page-container/sb-page-container.component'
            ).then((m) => m.SbPageContainerComponent),
        data: {
            breadcrumb: 'Funds',
            instrumentType: 'CERT'
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('./pages/authenticated/funds/funds.module').then(
                        (m) => m.FundsModule
                    )
            }
        ]
    },
    {
        path: 'generate-report',
        outlet: 'modal',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
            ).then((m) => m.SbModalDialogComponent),
        children: [
            {
                path: 'pdf',
                loadComponent: () =>
                    import(
                        './features/pdf-report/components/pdf-export-modal/pdf-export-modal.component'
                    ).then((m) => m.PdfExportModalComponent),
                data: {
                    title: 'Generate PDF report',
                    size: 'auto',
                    showBackdrop: true,
                    closeModal: true,
                    paramStrategy: 'merge'
                }
                // canActivate: [preserveQueryParamsGuard] Note: this would be useful but in the portfolio report, it bypasses the "mode" param on the initial navigation
                // We kinda want to get rid of the "report" mode on that page (as it only exists to trigger the startup modal which we might want to remove)
                // however, for now, keep the guard commented out
            }
        ]
    },
    {
        path: 'stress-test-scenario',
        outlet: 'dialog',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
            ).then((m) => m.SbModalDialogComponent),
        data: {
            title: 'Stress test scenario',
            showBackdrop: true,
            size: 'auto'
            //fullscreen: true
            //showFullscreenToggle: true
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import(
                        './pages/authenticated/risk/components/sb-risk/sb-risk.component'
                    ).then((m) => m.SbRiskComponent),
                children: []
            }
        ]
    },
    {
        path: 'portfolio',
        outlet: 'dialog',
        canActivate: [SessionGuard, KeycloakGuard],
        loadComponent: () =>
            import(
                './features/data-entry-flow/components/sb-modal-dialog/sb-modal-dialog.component'
            ).then((m) => m.SbModalDialogComponent),
        data: {
            title: 'Portfolio optimization',
            showBackdrop: true,
            size: 'lg',
            fullscreen: true
            //showFullscreenToggle: true
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import(
                        './features/portfolio-optimization/sb-portfolio-optimization/sb-portfolio-optimization.component'
                    ).then((m) => m.SbPortfolioOptimizationComponent),
                children: [
                    {
                        path: 'optimization',
                        loadComponent: () =>
                            import(
                                './features/portfolio-optimization/widgets/sb-estimated-returns/sb-estimated-returns.component'
                            ).then((m) => m.SbEstimatedReturnsComponent)
                    },
                    {
                        path: '',
                        redirectTo: 'optimization',
                        pathMatch: 'full'
                    }
                ]
            }
        ]
    },
    {
        path: '**',
        redirectTo: 'not-found'
    }
];

const routerOptions: ExtraOptions = {
    anchorScrolling: 'enabled',
    scrollPositionRestoration: 'enabled',
    paramsInheritanceStrategy: 'always'
};

@NgModule({
    imports: [RouterModule.forRoot(routes, routerOptions)],
    exports: [RouterModule]
})
export class AppRoutingModule {}
